import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import ms from "ms";
import { Suspense, useState } from "react";
import { Subheading } from "@/components/catalyst/Heading";
import { Select } from "@/components/catalyst/Select";
import { DateFnsFormat } from "@/components/DateFnsFormat";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/Tooltip";
import { useRefetchUser } from "@/hooks/use-refetch-user";
import { useRefreshableData } from "@/hooks/use-refreshable-data";
import { useSuccessQuery } from "@/hooks/use-success-query";
import { orpc } from "@/orpc/client";
import { formatCurrency } from "@/util/format";
import { AttributionsChart } from "./components/AttributionsChart";
import { OnboardingChecklist } from "./components/OnboardingChecklist";
import { PartnerLeaderboard } from "./components/PartnerLeaderboard";
import {
	PayoutHealthCard,
	PayoutHealthSkeleton,
	PayoutRiskAlert,
} from "./components/PayoutHealthCard";
import { RevenueByAppChart } from "./components/RevenueByAppChart";
import { RevenueChart } from "./components/RevenueChart";
import { StatCard } from "./components/StatCard";
import {
	ChartsSkeleton,
	OnboardingChecklistSkeleton,
	StatsSkeleton,
} from "./DashboardPageSkeleton";

const TIME_RANGE_OPTIONS = [
	{ value: 7, label: "Last 7 days" },
	{ value: 30, label: "Last 30 days" },
	{ value: 90, label: "Last 90 days" },
	{ value: 365, label: "Last year" },
] as const;

function DashboardStats({ days }: { days: number }) {
	const { data: stats } = useSuspenseQuery(
		orpc.dashboard.stats.queryOptions({
			input: { days },
		}),
	);

	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
			<StatCard
				label="Total Revenue"
				value={formatCurrency(stats.totalRevenue, stats.currency)}
				description="Excludes IAP fees & taxes"
			/>
			<StatCard
				label="Partner Earnings"
				value={formatCurrency(stats.totalPartnerEarnings, stats.currency)}
				description="Total earned by partners"
			/>
			<StatCard
				label="Estimated Payouts"
				value={formatCurrency(stats.estimatedPayouts, stats.currency)}
				description="Accrued, pending payout"
			/>
			<StatCard
				label="Attributions"
				value={stats.totalAttributions.toLocaleString()}
				description="New subscribers attributed"
			/>
			<StatCard
				label="Conversion Rate"
				value={`${stats.conversionRate.toFixed(1)}%`}
				description="Clicks → attributions"
			/>
		</div>
	);
}

function DashboardCharts({ days }: { days: number }) {
	const [revenue, attributions, revenueByApp, leaderboard] = [
		useSuspenseQuery(
			orpc.dashboard.revenueTimeSeries.queryOptions({
				input: { days },
			}),
		),
		useSuspenseQuery(
			orpc.dashboard.attributionsTimeSeries.queryOptions({
				input: { days },
			}),
		),
		useSuspenseQuery(
			orpc.dashboard.revenueByApp.queryOptions({
				input: { days },
			}),
		),
		useSuspenseQuery(
			orpc.dashboard.partnerLeaderboard.queryOptions({
				input: { days, limit: 10 },
			}),
		),
	];

	return (
		<>
			{/* Charts Row 1: Revenue + Pie Chart */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				<div className="lg:col-span-2">
					<h3 className="mb-2 text-lg font-semibold text-base-content">
						Revenue Over Time
					</h3>
					<RevenueChart data={revenue.data} />
				</div>
				<div>
					<h3 className="mb-2 text-lg font-semibold text-base-content">
						Revenue by App
					</h3>
					<RevenueByAppChart data={revenueByApp.data} />
				</div>
			</div>

			{/* Charts Row 2: Attributions */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
				<div className="lg:col-span-3">
					<h3 className="mb-2 text-lg font-semibold text-base-content">
						Attributions Over Time
					</h3>
					<AttributionsChart data={attributions.data} />
				</div>
			</div>

			{/* Charts Row 3: Partner Leaderboard */}
			<div>
				<h3 className="mb-2 text-lg font-semibold text-base-content">
					Top Partners
				</h3>
				<PartnerLeaderboard data={leaderboard.data} />
			</div>
		</>
	);
}

export function Dashboard() {
	const refetchUser = useRefetchUser();
	const [days, setDays] = useState<number>(30);

	const { mutateAsync: refreshDashboard } = useMutation(
		orpc.dashboard.refreshCache.mutationOptions(),
	);

	useSuccessQuery({
		from: "/_app/dashboard/",
		redirect: "/dashboard",
		matcher: "verify-email",
		successMessage: "Your email has been verified!",
		onSuccess: refetchUser,
	});

	const { canRefresh, isRefreshing, lastChecked, cooldownEndsAt, refresh } =
		useRefreshableData({
			queryKey: "dashboard",
			storageKey: "app_dashboard_last_refresh",
			cooldownMs: ms("10m"),
			onCacheBust: () => refreshDashboard({}),
		});

	const isRefreshDisabled = !canRefresh || isRefreshing;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<Subheading>Overview of your referral program performance</Subheading>
				<div className="flex items-center gap-4">
					{lastChecked && (
						<span className="text-xs text-base-content/60">
							Last checked: <DateFnsFormat date={lastChecked} relative />
						</span>
					)}
					<Tooltip>
						<TooltipTrigger
							onClick={isRefreshDisabled ? undefined : refresh}
							className={`rounded-md p-2 transition-colors ${
								isRefreshDisabled
									? "cursor-not-allowed opacity-50 text-base-content/60"
									: "cursor-pointer text-base-content/60 hover:bg-base-200 hover:text-base-content"
							}`}
						>
							{isRefreshing ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<RefreshCw className="size-4" />
							)}
						</TooltipTrigger>
						<TooltipContent>
							{isRefreshing ? (
								"Refreshing data..."
							) : canRefresh ? (
								"Refresh dashboard data"
							) : cooldownEndsAt ? (
								<>
									Refresh available{" "}
									<DateFnsFormat date={cooldownEndsAt} relative />
								</>
							) : (
								"Refresh on cooldown"
							)}
						</TooltipContent>
					</Tooltip>
					<Select
						value={days}
						onChange={(e) => setDays(Number(e.target.value))}
						className="w-48!"
					>
						{TIME_RANGE_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</Select>
				</div>
			</div>

			{/* Onboarding Checklist */}
			<Suspense fallback={<OnboardingChecklistSkeleton />}>
				<OnboardingChecklist />
			</Suspense>

			{/* Stats and Charts */}
			<Suspense fallback={null}>
				<PayoutRiskAlert days={days} />
			</Suspense>

			<Suspense fallback={<StatsSkeleton />}>
				<DashboardStats days={days} />
			</Suspense>

			<Suspense fallback={<PayoutHealthSkeleton />}>
				<PayoutHealthCard days={days} />
			</Suspense>

			<Suspense fallback={<ChartsSkeleton />}>
				<DashboardCharts days={days} />
			</Suspense>
		</div>
	);
}
